import { FC } from 'react';

import { useFormik } from 'formik';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { BoxesDistributionForm } from './form';

import { tournamentsService } from 'v2/api/tournamentsService';
import { Select } from 'v2/components/select/Select';
import { BoxDistributionType } from 'v2/constants';
import { TournamentModel } from 'v2/types/TournamentModel';
import { enumToSelect, handleHttpError } from 'v2/utils';

interface BoxesDistributionDialogProps {
  tournament: TournamentModel;
  isOpen: boolean;
  onClose: VoidFunction;
  onRevalidate: VoidFunction;
}

export const BoxesDistributionDialog: FC<BoxesDistributionDialogProps> = ({
  tournament,
  isOpen,
  onClose,
  onRevalidate,
}) => {
  const { trigger, isMutating } = useSWRMutation(
    ['sendBoxesToTournamentParticipants', tournament.id],
    tournamentsService.sendBoxesToTournamentParticipants,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The boxes were successfully shipped');
        handleClose();
      },
      onError: handleHttpError,
    }
  );

  const formik = useFormik<BoxesDistributionForm>({
    initialValues: { userCount: 1, distributionType: BoxDistributionType.TOP },
    onSubmit: (values) => {
      trigger(values);
    },
  });

  const handleTypeChange = (value: BoxDistributionType) => {
    formik.setFieldValue('distributionType', value);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Send Boxes</DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent className="form">
          <TextField
            name="userCount"
            value={formik.values.userCount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Number of users"
            type="number"
            required
          />

          <Select
            value={formik.values.distributionType}
            options={enumToSelect(BoxDistributionType)}
            label="Distribution Type"
            onChange={handleTypeChange}
            required
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button type="submit" variant="contained" loading={isMutating}>
            Send
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
